import { getColorClassNameAndStyle } from '@unifyapps/carbon/components/ColorPicker/utils/getColorClassNameAndStyle';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { getConditionalPropertyValue } from '@unifyapps/carbon/utils/conditionalValues';
import { createCustomIcon } from '@unifyapps/carbon/utils/createCustomIcon';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type { LCColorValue, StyleObject } from '@unifyapps/defs/types/styleObject';
import type { TypographyColors } from '@unifyapps/defs/types/typography';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import type { FeaturedIconColors } from '@unifyapps/ui/components/FeaturedIcon';
import { FeaturedIcon } from '@unifyapps/ui/components/FeaturedIcon';
import { clsx } from 'clsx';
import type { SyntheticEvent } from 'react';
import { useMemo } from 'react';
import { ICON_ID_CLASSNAME, iconVariants } from './style';

type Props = {
  icon?: string;
  hasClickEvent?: boolean;
  onClick?: (e: SyntheticEvent) => void;
  variant?: 'transparent' | 'featured';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  styles?: Pick<StyleObject, 'padding' | 'margin' | 'borderRadius'>;
  featured?: {
    color: string;
  };
  transparent?: {
    color: LCColorValue<TypographyColors>;
  };
  className?: string;
  customCSS?: string;
  customStyles: object | undefined;
  customVariantDataAttrs?: Record<string, string>;
};

export function Icon({
  icon,
  variant,
  size,
  styles,
  featured,
  transparent,
  hasClickEvent,
  onClick,
  className,
  customCSS,
  customStyles,
  customVariantDataAttrs,
}: Props) {
  const { className: blockAppearanceClassName, style } = useBlockAppearanceStyles({
    appearanceStyles: styles,
  });

  const { finalStyle } = useCustomStyles({
    customCSS,
    customStyles,
    style,
  });

  const iconName = getConditionalPropertyValue({ value: icon });
  const IconComponent = getIconFromRegistry(
    iconName,
    iconName ? createCustomIcon(iconName) : undefined,
  );

  // raw color value (stripping any conditional wrapper) — emitted as `data-color`
  // so theme CSS like `.ua-icon[data-color='text-primary'] { color: ... }` applies.
  // only the enum string case is useful as a data attribute; variable-mapped or
  // computed color values yield undefined and are dropped from the DOM.
  const colorAttrValue = useMemo(() => {
    const raw = getConditionalPropertyValue({
      value: variant === 'transparent' ? transparent?.color : featured?.color,
    });
    return typeof raw === 'string' ? raw : undefined;
  }, [variant, transparent?.color, featured?.color]);

  // getColorClassNameAndStyle emits a style (not className) for standard css colors like "fuchsia",
  // making colorClassName undefined for them and dropping the FeaturedIcon bg.
  const featuredColor = getConditionalPropertyValue({ value: featured?.color }) as
    | FeaturedIconColors
    | undefined;

  const { colorClassName, adaptedStyle } = useMemo(() => {
    const { className: colorClassName, style: colorStyle } = getColorClassNameAndStyle(
      transparent?.color,
      'color',
    );
    return {
      colorClassName,
      adaptedStyle: colorStyle
        ? {
            ...finalStyle,
            ...colorStyle,
          }
        : finalStyle,
    };
  }, [transparent?.color, finalStyle]);

  if (!IconComponent) {
    return null;
  }

  if (variant === 'transparent') {
    return (
      <IconComponent
        className={clsx(
          ICON_ID_CLASSNAME,
          iconVariants({ size }),
          blockAppearanceClassName,
          colorClassName,
          {
            'cursor-pointer': hasClickEvent,
          },
          className,
        )}
        data-color={colorAttrValue}
        data-size={size}
        onClick={onClick}
        style={adaptedStyle}
        {...customVariantDataAttrs}
      />
    );
  }

  return (
    <FeaturedIcon
      className={clsx(blockAppearanceClassName, { 'cursor-pointer': hasClickEvent }, className)}
      color={featuredColor}
      Icon={IconComponent}
      iconStyle={adaptedStyle}
      onClick={onClick}
      size={size}
      {...customVariantDataAttrs}
    />
  );
}
